<script lang="ts">
  /**
   * Tenant-edit-modal (S-04-34).
   *
   * Inline-modal i TenantsView. Tillåter Compuna-superadmin att uppdatera
   * `guest_policy` (Tier 1-gästregim) och `is_active` (soft-deactivate)
   * via PATCH /api/admin/tenants/{id}. Backend kräver MFA-step-up — en
   * stulen cookie räcker inte.
   *
   * ============================================================
   * UX-flow
   * ============================================================
   *  - Modal öppnas med pre-fyllda värden från tenant-raden.
   *  - Användaren ändrar guestPolicy (radio) eller isActive (checkbox).
   *  - Användaren matar in TOTP-koden (6 siffror).
   *  - "Spara" PATCH:ar till backend; vid 200 OK skickar `onSaved` den
   *    uppdaterade tenanten till TenantsView som mergar in i listan.
   *  - "Avbryt" / Esc / backdrop-klick → `onCancel` (om inte mid-save).
   *  - Inline-error vid `open_requires_attestation` (visa svensk
   *    åtgärdsinstruktion vid Öppen-radio), `invalid_mfa_code`,
   *    `mfa_locked`, `mfa_not_configured`.
   *
   * ============================================================
   * Hard-rules (CLAUDE.md)
   * ============================================================
   *  - MFA-step-up obligatoriskt (matchar admin-mönster, AdminUsersRoutes).
   *  - Inga råa databas-ID:n exponeras — backend returnerar UUID.
   *  - Inga fritext-fält — guestPolicy är enum, isActive är boolean.
   *  - guest_policy='open' returnerar 422 i MVP1 — UI markerar
   *    Öppen-radio invalid + visar svensk åtgärdsinstruktion.
   *  - Engelska kod-identifierare, svenska användartexter.
   *  - MFA-koden lagras inte mellan modal-öppningar (resetas på unmount
   *    via `cancel`-flödet och vid lyckad save).
   */
  import {
    guestPolicyLabel,
    tenantErrorMessage,
    updateTenant,
    type GuestPolicy,
    type Tenant,
  } from './tenantsClient';

  type Props = {
    /** Tenant att redigera (pre-fyller form-fälten). */
    tenant: Tenant;
    /** Anropas vid lyckad save med uppdaterad tenant från backend. */
    onSaved: (updated: Tenant) => void;
    /** Anropas vid Avbryt / Esc / backdrop-klick. Blockerad mid-save. */
    onCancel: () => void;
    /** Anropas vid 401/403 så TenantsView kan tvinga logout-flow. */
    onUnauthorized?: () => void;
  };

  const { tenant, onSaved, onCancel, onUnauthorized }: Props = $props();

  // ===================================================================
  // Form-state — pre-fyllt från tenant. Reaktivt vid prop-byte (om
  // modalen återanvänds för en annan rad).
  // ===================================================================

  let guestPolicy: GuestPolicy = $state(tenant.guestPolicy);
  let isActive = $state(tenant.isActive);
  let mfaCode = $state('');
  let isSaving = $state(false);
  let inlineError: string | null = $state(null);
  /**
   * Markerar Öppen-radio som invalid efter ett 422-svar — användaren
   * ska kunna byta till en annan policy utan att felmeddelandet sitter
   * fast i evighet. Resetas vid varje försök.
   */
  let openPolicyInvalid = $state(false);

  let mfaInputElement: HTMLInputElement | null = $state(null);

  // Auto-fokusera MFA-fältet när modalen öppnas så användaren kan börja
  // skriva direkt utan extra klick. Triggas på mount via $effect.
  $effect(() => {
    if (mfaInputElement) {
      // Defer en tick så modalens transition hinner appliceras innan
      // fokus stjäls (annars kan fokus-ringen flicka).
      queueMicrotask(() => mfaInputElement?.focus());
    }
  });

  function handleCancel(): void {
    if (isSaving) return;
    onCancel();
  }

  function handleBackdropClick(): void {
    handleCancel();
  }

  function handleEscape(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      handleCancel();
    }
  }

  function selectGuestPolicy(value: GuestPolicy): void {
    guestPolicy = value;
    // Användaren bytte aktivt policy — clear öppen-invalid-flaggan så
    // att felmeddelandet inte sitter kvar efter en byte till annan policy.
    if (value !== 'open') {
      openPolicyInvalid = false;
      if (inlineError) inlineError = null;
    }
  }

  async function handleSubmit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (isSaving) return;

    inlineError = null;
    isSaving = true;
    try {
      const result = await updateTenant(
        tenant.id,
        { guestPolicy, isActive },
        mfaCode,
      );

      switch (result.kind) {
        case 'ok':
          // Rensa MFA-koden direkt så den inte ligger kvar i memory om
          // modalen återanvänds (defensivt — anroparen avmonterar oss
          // ändå normalt).
          mfaCode = '';
          openPolicyInvalid = false;
          onSaved(result.tenant);
          return;
        case 'unauthorized':
          inlineError = tenantErrorMessage('unauthorized');
          onUnauthorized?.();
          return;
        case 'not_found':
          inlineError = tenantErrorMessage('not_found');
          return;
        case 'invalid_mfa_code':
          inlineError = tenantErrorMessage('invalid_mfa_code');
          mfaCode = '';
          mfaInputElement?.focus();
          return;
        case 'mfa_locked':
          inlineError = tenantErrorMessage('mfa_locked');
          return;
        case 'mfa_not_configured':
          inlineError = tenantErrorMessage('mfa_not_configured');
          return;
        case 'open_requires_attestation':
          inlineError = tenantErrorMessage('open_requires_attestation');
          openPolicyInvalid = true;
          return;
        case 'invalid_guest_policy':
          inlineError = tenantErrorMessage('invalid_guest_policy');
          return;
        case 'failure':
          inlineError = result.message;
          return;
      }
    } finally {
      isSaving = false;
    }
  }

  // De tre tillåtna värdena renderas i en stabil ordning. invitation_only
  // är default i MVP1 (matchar pilot-läget) och placeras först.
  const POLICY_OPTIONS: GuestPolicy[] = ['invitation_only', 'hybrid', 'open'];
</script>

<div
  class="modal-backdrop"
  role="presentation"
  onclick={handleBackdropClick}
  onkeydown={handleEscape}
>
  <div
    class="modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="tenant-edit-title"
    tabindex="-1"
    onclick={(e) => e.stopPropagation()}
    onkeydown={(e) => e.stopPropagation()}
  >
    <h3 id="tenant-edit-title">Redigera tenant</h3>
    <dl class="details">
      <dt>Namn</dt>
      <dd>{tenant.name}</dd>
      <dt>Slug</dt>
      <dd><code>{tenant.slug}</code></dd>
      <dt>Tenant-id</dt>
      <dd><code>{tenant.id}</code></dd>
    </dl>

    <form class="form" onsubmit={handleSubmit}>
      <fieldset class="field-group">
        <legend>Gäst-policy</legend>
        <p class="field-hint">
          Avgör om gäst-användare (Tier 1, utan identitet) får ansluta. Endast inbjudan
          är default i MVP1; öppen kräver enhets-attestation som inte finns ännu.
        </p>
        {#each POLICY_OPTIONS as option (option)}
          <label
            class="radio-row"
            class:invalid={option === 'open' && openPolicyInvalid}
          >
            <input
              type="radio"
              name="guest-policy"
              value={option}
              checked={guestPolicy === option}
              onchange={() => selectGuestPolicy(option)}
              disabled={isSaving}
            />
            <span class="radio-label">{guestPolicyLabel(option)}</span>
            {#if option === 'open'}
              <span class="radio-tag" aria-hidden="true">kräver attestation</span>
            {/if}
          </label>
        {/each}
      </fieldset>

      <div class="field checkbox-field">
        <label class="checkbox-row">
          <input
            type="checkbox"
            bind:checked={isActive}
            disabled={isSaving}
          />
          <span>Aktiv tenant (avmarkera för att soft-deaktivera)</span>
        </label>
        <p class="field-hint">
          Soft-deaktiverad tenant tar inte emot nya konton men befintliga walks
          och rapporter bevaras. Det finns ingen hård-radering.
        </p>
      </div>

      <div class="field">
        <label for="tenant-edit-mfa">Din MFA-kod (TOTP)</label>
        <input
          id="tenant-edit-mfa"
          type="text"
          inputmode="numeric"
          pattern="[0-9]{'{6}'}"
          maxlength="6"
          bind:value={mfaCode}
          bind:this={mfaInputElement}
          required
          autocomplete="one-time-code"
          placeholder="123456"
          disabled={isSaving}
        />
        <p class="field-hint">
          Sex siffror från din authenticator-app. Krävs för att skydda mot stulna cookies.
        </p>
      </div>

      {#if inlineError}
        <div class="alert error" role="alert">{inlineError}</div>
      {/if}

      <div class="modal-actions">
        <button
          type="button"
          class="btn secondary"
          onclick={handleCancel}
          disabled={isSaving}
        >
          Avbryt
        </button>
        <button type="submit" class="btn primary" disabled={isSaving}>
          {isSaving ? 'Sparar…' : 'Spara'}
        </button>
      </div>
    </form>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    z-index: 80;
  }
  .modal {
    background: var(--bg-elev, #1a1a1c);
    border: 1px solid var(--rule, #2a2a2e);
    border-radius: 8px;
    padding: 1.5rem;
    width: min(560px, 100%);
    max-height: calc(100vh - 2rem);
    overflow-y: auto;
    color: var(--text, #e8e8ea);
    font-family: inherit;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.45);
  }
  h3 {
    margin: 0 0 1rem;
    font-size: 1.2rem;
  }
  .details {
    margin: 0 0 1.25rem;
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.35rem 0.85rem;
    font-size: 0.88rem;
  }
  .details dt {
    color: var(--text-dim, #a4a4a8);
    font-weight: 600;
  }
  .details dd {
    margin: 0;
  }
  .details code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.82rem;
    color: var(--text-dim, #a4a4a8);
  }
  .form {
    display: grid;
    gap: 1rem;
  }
  .field {
    display: grid;
    gap: 0.35rem;
  }
  .field-group {
    border: 1px solid var(--rule, #2a2a2e);
    border-radius: 6px;
    padding: 0.75rem 1rem 0.85rem;
    margin: 0;
  }
  .field-group legend {
    padding: 0 0.4rem;
    font-size: 0.85rem;
    color: var(--text-dim, #a4a4a8);
  }
  .field-hint {
    margin: 0.15rem 0 0;
    color: var(--text-dim, #a4a4a8);
    font-size: 0.8rem;
  }
  .radio-row {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    padding: 0.4rem 0;
    cursor: pointer;
  }
  .radio-row.invalid .radio-label {
    color: #c0533f;
  }
  .radio-label {
    font-size: 0.95rem;
  }
  .radio-tag {
    margin-left: auto;
    font-size: 0.72rem;
    color: var(--text-dim, #a4a4a8);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border: 1px solid var(--rule, #2a2a2e);
    border-radius: 999px;
    padding: 0.1rem 0.55rem;
  }
  .checkbox-field {
    border: 1px solid var(--rule, #2a2a2e);
    border-radius: 6px;
    padding: 0.75rem 1rem;
  }
  .checkbox-row {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    cursor: pointer;
  }
  input[type='text'] {
    background: transparent;
    border: 1px solid var(--rule, #2a2a2e);
    color: var(--text, #e8e8ea);
    border-radius: 4px;
    padding: 0.55rem 0.7rem;
    font-size: 0.95rem;
    font-family: inherit;
  }
  input[type='text']:focus {
    border-color: var(--glow, #e8a64a);
    outline: 2px solid var(--accent, #c8862c);
    outline-offset: 1px;
  }
  input:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  .alert.error {
    background: var(--bg-elev, #1a1a1c);
    border: 1px solid #c0533f;
    border-left: 3px solid #c0533f;
    border-radius: 4px;
    padding: 0.65rem 0.85rem;
    color: var(--text, #e8e8ea);
    font-size: 0.88rem;
  }
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
    margin-top: 0.5rem;
  }
  .btn {
    padding: 0.55rem 1.1rem;
    border-radius: 6px;
    font-size: 0.92rem;
    cursor: pointer;
    font-family: inherit;
    border: 1px solid transparent;
  }
  .btn.secondary {
    background: transparent;
    border-color: var(--rule, #2a2a2e);
    color: var(--text, #e8e8ea);
  }
  .btn.secondary:hover:not(:disabled) {
    border-color: var(--text-dim, #a4a4a8);
  }
  .btn.primary {
    background: var(--accent, #c8862c);
    color: #1a1a1c;
    font-weight: 600;
  }
  .btn.primary:hover:not(:disabled) {
    background: var(--glow, #e8a64a);
  }
  .btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
</style>
